import { useMemo } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { FaArrowRight, FaCubes, FaLeaf } from "react-icons/fa";
import { BiEditAlt, BiPlusCircle, BiTrash } from "react-icons/bi";
import { MdContentCopy } from "react-icons/md";
import { getModuleNameFromBEId } from "@utils/moduleList";
import type { Line } from "@components/kebabMenu/KebabMenu";
import type { FastActivity } from "@typedefs/interfaces";
import { useCreateActivityCopyMutation } from "@hooks/index";
import { useAppDispatch, useAppNavigate, useAppParams, useAppSelector, useIconSize, usePermissions } from "@app/hooks";
import { selectCurrentProject } from "@features/project/projectSlice";
import { setBIntactMapState, setModuleList } from "@features/builder/builderSlice";
import { encodeActivityId } from "@utils/projectIdEncoder";
import { EventTypes, useFirebaseHook } from "@utils/firebaseUtils";
import TranslatableText from "@components/translations/TranslatableText";
import Button from "@components/button/Button";
import KebabMenu from "@components/kebabMenu/KebabMenu";
import { BIntactMapState } from "@app/features/builder/builderTypes";

const ActivityCard = ({
	activity,
	onDelete,
	onError,
}: {
	activity: FastActivity;
	onDelete: () => void;
	onError?: (error: string) => void;
}) => {
	const isBIntact = activity.is_b_intact;
	const { activity: currentActivity, project } = useAppSelector(selectCurrentProject)
	const { rawProjectId } = useAppParams();
	const { isReadOnly, canMutateProject, isPublic } = usePermissions();
	const { fireEvent } = useFirebaseHook();
	const navigate = useAppNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();
	const [duplicateActivity, { isLoading: isDuplicateLoading }] =
		useCreateActivityCopyMutation();
	const iconSize = useIconSize(16);

	const handleCardClick = () => {
		const activityIdToUse = isPublic
			? (() => {
				try {
					return encodeActivityId(activity.id);
				} catch (error) {
					console.error('Failed to encode activity ID:', error);
					return activity.id.toString();
				}
			})()
			: activity.id;

		if (activity.id !== currentActivity?.id)
			dispatch(setModuleList([]))

		if (!isBIntact) {
			return navigate({ path: `/builder/${activityIdToUse}` });
		}

		dispatch(setBIntactMapState(BIntactMapState.DRAW_ACTIVITY));
		if (project?.id)
			navigate({ path: `/bintact-builder/${rawProjectId}/${activityIdToUse}`, options: { state: { from: location.pathname } } });
	}

	const progress = useMemo(() => {
		const completition = activity?.completion_percentage ?? 0;
		return completition * 100 < 5 ? 5 : completition * 100;
	}, [activity.completion_percentage]);

	const isEmpty = useMemo(
		() => activity?.completion_percentage === 0,
		[activity.completion_percentage],
	);
	const lines: Line[] = useMemo(
		(): Line[] => [
		{
			id: 0,
			label: "main.edit",
			icon: BiEditAlt,
			clickHandler: () => navigate({ path: `/activity/${activity.id}/edit` }),
		},
			...(!isBIntact ? [{
				id: 1,
				label: "main.duplicate",
				icon: MdContentCopy,
				clickHandler: async () => {
					if (isDuplicateLoading) return;
					try {
						await duplicateActivity(activity.id).unwrap();
						fireEvent(EventTypes.duplicate_activity);
					} catch (error) {
						if (typeof error === "string") {
							onError?.(error);
						} else {
							onError?.(`Error duplicating activity ${activity.id}`);
						}
					}
				},
			} as Line] : []),
			...(canMutateProject && !isBIntact ? [{
				id: 3,
				label: "main.delete",
				icon: BiTrash,
				clickHandler: onDelete,
			} as Line] : []),
		],
		[onDelete, canMutateProject, isDuplicateLoading, onError, navigate, fireEvent, duplicateActivity, isBIntact, activity],
	);

	const isAreaDefined = useMemo(() => activity.module_types.length > 0, [activity.module_types]);
	const footerButtonText = useMemo(() => isReadOnly ? "activity.view_activity" :
		isBIntact ? isAreaDefined ? "activity.edit_area_on_map" : "activity.define_area_on_map" :
			isEmpty ? "activity.perform_assessment" : "activity.edit_assessment", [isReadOnly, isEmpty, isBIntact, isAreaDefined])

	return (
		<motion.div
			className="activity-card"
			whileHover={{ boxShadow: "5px 5px 10px rgba(73, 28, 105, 0.3)" }}
		>
			<div className="content">
				<div className="d-flex f-column">
					<span className="fs-11 text-gray lh-90"><TranslatableText translationKey="main.activity" /> {activity.id}</span>
					<h3 className="fs-11 ff-medium text-common-dark">{activity.name}</h3>
				</div>

				{!isReadOnly ? <KebabMenu lines={lines} activeColor="var(--quaternary-dark)" /> : <div></div>}
				<div className="d-flex f-column">
					{activity.module_types.length > 0 &&
						<>
							<div className="fs-11 text-gray" >
								<TranslatableText translationKey="main.modules" /> :
							</div>
							<ul>
								{activity.module_types.map((mod, j) => (
									<li
										className="ff-normal-cond fs-11"
										style={{ paddingLeft: "0.2rem" }}
										key={j}
									>
										<TranslatableText translationKey={getModuleNameFromBEId(mod) ?? ""} />
									</li>
								))}
							</ul>
						</>
					}
				</div>
				<div>
					{isBIntact ? <FaLeaf color="var(--bintact-bright)" size={28} /> : <FaCubes color="var(--gray)" size={28} />}
				</div>
			</div>
			<Button
				className={"footer"}
				whileHoverProps={{ scale: 1, opacity: 0.9 }}
				whileTapProps={{ scale: 1, opacity: 0.95 }}
				style={{
					borderImage: `linear-gradient(90deg, #b53f74 ${progress}%, white 0%, white 100%) 1`,
				}}
				onClick={handleCardClick}
			>
				<div>
					<FaArrowRight size={iconSize} color="white" />
				</div>
				<TranslatableText className="fs-11 ff-medium d-flex align-items-center ps-1 h-100" translationKey={footerButtonText} />
			</Button>
		</motion.div>
	);
};

export const CreateNewActivityCard = ({	onOpenModal }: { onOpenModal?: () => void }) => {
	const { isReadOnly } = usePermissions();
	const iconSize = useIconSize(18);

	const handleNewClick = () => {
		if (isReadOnly) return;
		onOpenModal?.();
	};

	return (
		<div
			className={`activity-card justify-content-between ${isReadOnly ? "op-5" : ""}`}
			style={{ height: "108px !important" }}
		>
			<div className="content">
				<h3 className="fs-11 ff-medium text-common-dark">
					<TranslatableText translationKey="activity.create_new_activity" />
				</h3>
			</div>

			<Button
				className="footer create justify-content-between"
				whileHoverProps={{ scale: 1, opacity: 0.9 }}
				whileTapProps={{ scale: 1, opacity: 0.95 }}
				style={{
					borderImage:
						"linear-gradient(90deg, var(--quaternary-dark) 10%, white 0%, white 100%) 1",
				}}
				onClick={handleNewClick}
				disabled={isReadOnly}
			>
				<TranslatableText className="fs-11 ff-medium d-flex align-items-center ps-1 h-100" translationKey="main.create_new" />
				<div>
					<BiPlusCircle size={iconSize} color="white" />
				</div>
			</Button>
		</div>
	);
};

export default ActivityCard;
